<script setup lang="ts">
/**
 * CriticalAlertModal — Full-screen blocking overlay for CRITICAL priority notifications.
 *
 * Mounted in AppLayout, visible on ALL authenticated pages.
 * When a CRITICAL priority notification arrives (e.g. CLASS_I PRODUCT_RECALLED,
 * ALLERGY_ALERT), this modal takes over the entire screen. The user MUST click
 * "Acknowledge" to dismiss and continue working.
 *
 * If multiple CRITICAL alerts queue up, they drain one at a time (FIFO).
 *
 * Design:
 *  - Semi-transparent dark overlay (z-50, above everything)
 *  - Red-themed urgent card with alert icon
 *  - Subject + rendered body + deep-link to recall
 *  - "I Acknowledge" button → PATCH /notifications/:id/actioned
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import { Icon, useToaster } from '@tms-phoenix/ui-vue';
import { useNotificationStore } from '@/stores';
import { getApiClient } from '@/core/services/api.service';
import { useNotificationContentLocalization } from '@/shared/composables/useNotificationContentLocalization';

const { t } = useTranslation();
const router = useRouter();
const notificationStore = useNotificationStore();
const toast = useToaster();
const { localizeSubject, localizeBody } = useNotificationContentLocalization();

const isDismissing = ref(false);

const hasCritical = computed(() => notificationStore.hasCriticalAlert);
const currentAlert = computed(() => notificationStore.currentCriticalAlert);
const pendingCount = computed(() => notificationStore.criticalAlertCount);
const displaySubject = computed(() =>
  currentAlert.value ? localizeSubject(currentAlert.value.event_type, currentAlert.value.subject) : '',
);
const displayBody = computed(() =>
  currentAlert.value ? localizeBody(currentAlert.value.event_type, currentAlert.value.rendered_body) : '',
);

/**
 * Dismiss the current critical alert via the API.
 * Throws on failure so callers can handle the error contextually.
 */
async function dismissCurrentAlert(): Promise<void> {
  if (!currentAlert.value) return;
  const api = getApiClient();
  await notificationStore.dismissCriticalAlert(currentAlert.value.notification_id, api);
}

async function handleAcknowledge() {
  if (isDismissing.value) return;
  isDismissing.value = true;
  try {
    await dismissCurrentAlert();
  } catch (err) {
    console.error('[CriticalAlertModal] Failed to dismiss critical alert:', err);
    toast.danger(t('notifications.critical.acknowledgeError', 'Failed to acknowledge alert. Please try again.'));
  } finally {
    isDismissing.value = false;
  }
}

async function handleNavigateToRecall() {
  if (isDismissing.value || !currentAlert.value?.reference_entity_id) return;
  const recallId = currentAlert.value.reference_entity_id;
  isDismissing.value = true;

  // Dismiss then navigate — keep modal blocking on failure
  try {
    await dismissCurrentAlert();
    router.push(`/recall/${recallId}/impact`);
  } catch (err) {
    console.error('[CriticalAlertModal] Failed to acknowledge alert:', err);
    toast.danger(t('notifications.critical.acknowledgeError', 'Failed to acknowledge alert. Please try again.'));
  } finally {
    isDismissing.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="hasCritical && currentAlert"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        role="alertdialog"
        aria-modal="true"
        :aria-label="t('notifications.critical.ariaLabel', { subject: displaySubject })"
      >
        <div class="w-full max-w-lg mx-4 rounded-xl bg-white shadow-2xl overflow-hidden animate-slide-up">
          <!-- Header -->
          <div class="flex items-center gap-3 bg-red-600 px-6 py-4 text-white">
            <!-- Warning icon -->
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <Icon
                name="IconWarning"
                size-class="h-6 w-6"
                variant="stroke"
                color="text-white"
              />
            </div>
            <div>
              <h2 class="text-lg font-bold">
                {{ t('notifications.critical.title') }}
              </h2>
              <p class="text-sm text-red-100">
                {{ t('notifications.critical.subtitle') }}
              </p>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 py-5 space-y-4">
            <div>
              <h3 class="text-base font-semibold text-gray-900">
                {{ displaySubject }}
              </h3>
              <p class="mt-2 text-sm text-gray-600 leading-relaxed">
                {{ displayBody }}
              </p>
            </div>

            <!-- Deep-link to recall detail -->
            <div
              v-if="currentAlert.reference_entity_type === 'RECALL' || currentAlert.reference_entity_type === 'PRODUCT_RECALL'"
              class="rounded-lg bg-red-50 border border-red-200 px-4 py-3"
            >
              <p class="text-xs text-red-700 font-medium">
                {{ t('notifications.critical.recallLinked') }}
              </p>
              <button
                type="button"
                class="mt-1 text-sm font-semibold text-red-700 hover:text-red-900 underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isDismissing"
                @click="handleNavigateToRecall"
              >
                {{ t('notifications.critical.goToRecall') }} &rarr;
              </button>
            </div>

            <!-- Event type indicator -->
            <div class="flex items-center gap-2 text-xs text-gray-400">
              <span class="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span>{{ t('notifications.critical.priorityLabel') }}</span>
              <span v-if="currentAlert.event_type"> &middot; {{ currentAlert.event_type }}</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4">
            <span
              v-if="pendingCount > 1"
              class="text-xs text-gray-500"
            >
              {{ t('notifications.critical.pendingCount', { count: pendingCount - 1 }) }}
            </span>
            <span v-else />

            <Button
              severity="danger"
              class="font-semibold"
              :loading="isDismissing"
              :disabled="isDismissing"
              @click="handleAcknowledge"
            >
              <Icon
                name="IconSuccess"
                size-class="h-4 w-4 mr-2"
                variant="stroke"
                color="text-white"
              />
              {{ t('notifications.critical.acknowledge') }}
            </Button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}
</style>
